<template>
  <div class="editor-shell">
    <Toolbar
      class="toolbar"
      :editor="editorRef"
      :default-config="toolbarConfig"
      mode="default"
    />
    <Editor
      v-model="htmlValue"
      class="editor-body"
      :default-config="editorConfig"
      mode="default"
      @on-created="handleCreated"
    />
  </div>
</template>

<script setup>
import '@wangeditor/editor/dist/css/style.css';

import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';

import { getToken } from '../utils/auth';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);

const baseApi = process.env.VUE_APP_API_BASE || '/api';
const editorRef = shallowRef();
const htmlValue = ref(props.modelValue);
const toolbarConfig = {
  toolbarKeys: [
    'headerSelect',
    'bold',
    'italic',
    'underline',
    'through',
    'color',
    'bgColor',
    'bulletedList',
    'numberedList',
    'blockquote',
    'insertLink',
    'uploadImage',
    'uploadVideo',
    'insertTable',
    'codeBlock',
    'undo',
    'redo'
  ]
};

const buildHeaders = () => ({
  Authorization: `Bearer ${getToken()}`
});

const editorConfig = computed(() => ({
  placeholder: props.placeholder,
  autoFocus: false,
  MENU_CONF: {
    uploadImage: {
      server: `${baseApi}/upload/single`,
      fieldName: 'file',
      maxFileSize: 20 * 1024 * 1024,
      headers: buildHeaders(),
      customInsert(res, insertFn) {
        if (res?.data?.file_url) {
          insertFn(res.data.file_url, res.data.file_name, res.data.file_url);
        }
      }
    },
    uploadVideo: {
      server: `${baseApi}/upload/single?type=video`,
      fieldName: 'file',
      maxFileSize: 200 * 1024 * 1024,
      headers: buildHeaders(),
      customInsert(res, insertFn) {
        if (res?.data?.file_url) {
          insertFn(res.data.file_url, '');
        }
      }
    }
  }
}));

const handleCreated = (editor) => {
  editorRef.value = editor;
};

watch(
  () => props.modelValue,
  (value) => {
    if (value !== htmlValue.value) {
      htmlValue.value = value;
    }
  }
);

watch(htmlValue, (value) => {
  emit('update:modelValue', value);
});

onBeforeUnmount(() => {
  const editor = editorRef.value;
  if (editor) {
    editor.destroy();
  }
});
</script>

<style scoped>
.editor-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  min-width: 0;
}

.toolbar {
  border-bottom: 1px solid #e5e7eb;
}

.editor-body {
  min-height: 420px;
}

.editor-body :deep(.w-e-text-container) {
  min-height: 420px;
}

@media (max-width: 768px) {
  .editor-body,
  .editor-body :deep(.w-e-text-container) {
    min-height: 320px;
  }
}
</style>
